import { useEffect, useState, type CSSProperties, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { apiRequest, openApiFile } from '../api/client.js';
import { useAuth } from '../auth/AuthProvider.js';
import { useTranslation } from '../../../shared/i18n/index.js';
import { useBusinessDay } from '../../../shared/hooks/useBusinessDay.js';
import { useBusinessNumber } from '../../../shared/hooks/useBusinessNumber.js';
import { unitLabel } from '../../../shared/contracts/unit-label.js';
import {
  siteHistoryOfJobPath,
  siteWorkBranding,
  siteWorkPhotoPath,
  siteWorkStageText,
  type SiteHistory,
  type SiteWorkEntry,
} from '../../../shared/contracts/site-history.js';

/**
 * R14 — the painting already done, or still in progress, at a site and for
 * its dealer.
 *
 * `LastWorkList` is the one rendering of it: the creation popup and every
 * approval level show exactly this — the count within the distance,
 * highlighted, then each job with where it stands — so the approver reads
 * what the raiser was shown.
 */
export function LastWorkList({ history }: { readonly history: SiteHistory }): ReactNode {
  const { t } = useTranslation();
  const { day, atDay } = useBusinessDay();
  const { decimal, number } = useBusinessNumber();
  const { token } = useAuth();
  const [photoError, setPhotoError] = useState<string | null>(null);

  // Fetched rather than linked so the Authorization header travels with it —
  // the same way the review screen shows a job's photographs.
  async function openPhoto(entry: SiteWorkEntry): Promise<void> {
    const path = siteWorkPhotoPath(entry);
    if (!path) return;
    setPhotoError(null);
    try {
      await openApiFile(path, token);
    } catch (err) {
      setPhotoError(t('siteHistory.photoFailed', { error: (err as Error).message }));
    }
  }

  return (
    <>
      {history.located ? (
        <p aria-label="last-work-summary" style={SUMMARY}>
          {t('siteHistory.summary', {
            radius: number(history.radiusMeters, 0),
            done: String(history.nearby.done),
            inProgress: String(history.nearby.inProgress),
          })}
        </p>
      ) : null}
      <table aria-label="last-work-list" style={TABLE}>
        <thead>
          <tr>
            <th style={TH}>{t('siteHistory.col.date')}</th>
            <th style={TH}>{t('siteHistory.col.job')}</th>
            <th style={TH}>{t('siteHistory.col.customer')}</th>
            <th style={TH}>{t('siteHistory.col.branding')}</th>
            <th style={TH}>{t('siteHistory.col.vendor')}</th>
            <th style={TH}>{t('siteHistory.col.area')}</th>
            <th style={TH}>{t('siteHistory.col.stage')}</th>
            <th style={TH}>{t('siteHistory.col.distance')}</th>
            <th style={TH}>{t('siteHistory.col.photo')}</th>
          </tr>
        </thead>
        <tbody>
          {history.entries.map((e) => (
            <tr key={e.jobRequestId} aria-label={`last-work-row-${e.jobRequestId}`}>
              <td style={TD}>{day(e.requestDate)}</td>
              <td style={TD}>
                <Link to={`/jobs/${e.jobRequestId}/journey`}>{e.jobNumber ?? '—'}</Link>
              </td>
              <td style={TD}>{e.dealerCode ? `${e.customerName} (${e.dealerCode})` : e.customerName}</td>
              <td style={TD}>{siteWorkBranding(e)}</td>
              <td style={TD}>{e.vendorName ?? '—'}</td>
              <td style={TD}>
                {decimal(e.areaRequested)} {unitLabel(e.areaUnit)}
                {e.executedArea != null
                  ? ` · ${t('siteHistory.executed', { area: `${decimal(e.executedArea)} ${unitLabel(e.executedAreaUnit ?? e.areaUnit)}` })}`
                  : null}
              </td>
              <td style={TD} aria-label={`last-work-stage-${e.jobRequestId}`}>
                <strong>{siteWorkStageText(e, t, atDay)}</strong>
                <br />
                <small>{e.currentStatus}</small>
              </td>
              <td style={TD}>
                {e.distanceMeters != null ? t('siteHistory.metres', { m: number(e.distanceMeters, 0) }) : '—'}
                {e.sameDealer ? (
                  <>
                    <br />
                    <small aria-label={`last-work-same-dealer-${e.jobRequestId}`}>{t('siteHistory.sameDealer')}</small>
                  </>
                ) : null}
              </td>
              <td style={TD}>
                {e.photoId ? (
                  <button type="button" aria-label={`last-work-photo-${e.jobRequestId}`} onClick={() => { void openPhoto(e); }}>
                    {t('siteHistory.viewPhoto')}
                  </button>
                ) : '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {photoError ? <p role="alert">{photoError}</p> : null}
    </>
  );
}

/**
 * The same history on an approval level, read for the job being decided. A
 * reader who may not see it is told so rather than shown an empty panel that
 * looks like "nothing was ever done here".
 */
export function LastWorkPanel({ jobRequestId }: { readonly jobRequestId: string }): ReactNode {
  const { t } = useTranslation();
  const { token } = useAuth();
  const { at } = useBusinessDay();
  const [history, setHistory] = useState<SiteHistory | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setHistory(null);
    setError(null);
    apiRequest<SiteHistory>(siteHistoryOfJobPath(jobRequestId), { token })
      .then((r) => { if (alive) setHistory({ ...r, entries: Array.isArray(r?.entries) ? r.entries : [] }); })
      .catch((e) => { if (alive) setError((e as Error).message); });
    return () => { alive = false; };
  }, [jobRequestId, token]);

  const ack = history?.acknowledgement ?? null;
  return (
    <section aria-label="last-work-panel" style={{ margin: '1rem 0' }}>
      <h2 style={{ fontSize: '1rem', margin: '0 0 0.5rem' }}>{t('siteHistory.title')}</h2>
      {error ? (
        <p role="alert">{t('siteHistory.failed', { error })}</p>
      ) : !history ? (
        <p role="status">{t('siteHistory.loading')}</p>
      ) : (
        <>
          {!history.located ? <p aria-label="last-work-not-located">{t('siteHistory.notLocated')}</p> : null}
          {history.entries.length === 0 ? (
            history.located ? <p aria-label="last-work-none">{t('siteHistory.none', { radius: String(history.radiusMeters) })}</p> : null
          ) : (
            <LastWorkList history={history} />
          )}
        </>
      )}
      {ack ? (
        <p aria-label="last-work-acknowledgement">
          {t(ack.acknowledged ? 'siteHistory.ackYes' : 'siteHistory.ackNo', {
            user: ack.recordedByName ?? '—',
            count: String(ack.shownJobRequestIds.length),
            date: at(ack.recordedAt),
          })}
        </p>
      ) : null}
    </section>
  );
}

const TABLE: CSSProperties = { borderCollapse: 'collapse', width: '100%', fontSize: '0.875rem' };
/** Highlighted: the first thing the raiser and every approver must read. */
const SUMMARY: CSSProperties = {
  margin: '0 0 0.5rem',
  padding: '0.5rem 0.75rem',
  fontWeight: 600,
  borderLeft: '4px solid var(--color-warning)',
  background: 'var(--color-warning-bg)',
};
const TH: CSSProperties = { textAlign: 'left', padding: '0.25rem 0.5rem', borderBottom: '1px solid var(--color-border, #d8dde6)' };
const TD: CSSProperties = { padding: '0.25rem 0.5rem', borderBottom: '1px solid var(--color-border, #eef1f6)', verticalAlign: 'top' };
